import { DeleteFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Button, InputNumber, Modal, Select, Spin, Table } from 'antd';
import cx from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { deviceAdminApi, enrollmentApi } from '../../../../../api';
import { buildColumn, ColumnsProps, filterOption, noneDecimalFormatter } from '../../../../../Helper';
import { BaseColumns } from '../../../../../model/CommonModel';
import { CredentialCheckConflict } from '../../../../../model/CredentialModel';
import { ControlZone, DoorGroup } from '../../../../../model/DeviceAdminModel';
import { CredentialFunction, FunctionGroup, FunctionModel, PanelZone, ThreatLevel, XboxController } from '../../../../../model/EnrollmentModel';
import { Select as SelectCommon } from '../../../../common';
import styles from '../../credentialmodal.module.scss';
import {
	setControlZonesArrayAction,
	setCredentialFunctionsArrayAction,
	setFunctionTabServicesDataAction,
	setRootModelPropertyAction,
} from '../../credentialTemplateContext/actions';
import { CredentialStoreContext } from '../../credentialTemplateContext/context';
import { filterDoorGroups } from '../../Helper';

enum CredentialFunctionEnum {
	CONTROL_ZONE,
	DOOR_GROUP,
	FUNCTION_GROUP,
	THREAT_LEVEL,
	INDEXED_COMMAND,
	DEADMAN_TIMER,
	SPECIAL_DOOR_GROUP,
}

const MAX_DEADMAN_SECONDS: number = 65000;
type CredentialFunctionsDataType = CredentialFunction & BaseColumns;
type AddCredentialFunction = {
	credentialFunction: CredentialFunctionEnum;
	controlZoneIndex?: number;
	controllerIndex?: number;
};

export const CredentialTemplateModalFunctionsTab: React.FC = () => {
	const {
		credentialInitialState: {
			configData: { CCMVersionSupportsHRecord, CustomAccessZones, IsDeviceAdminCredentialTemplate, OverlapAccessFlag, IndexedCommandsOptions },
			fetchedCredential,
			functionsTabState: { controllers, controlZones, credentialFunctions, doorGroups, functionCategories, functionGroups },
			generalTabState: {
				cardSectionState: { saveConcatenatedData },
				IDF,
				informationSectionState: { credentialTemplates, linkToCredential },
			},
			modelEstablished,
			limitsTabState: { threatLevels },
		},
		dispatcher,
	} = useContext(CredentialStoreContext);

	const [controller, setController] = useState<number>(-1);
	const [controlZone, setControlZone] = useState<number>(-1);
	const [doorGroup, setDoorGroup] = useState<string>('');
	const [localDoorGroups, setLocalDoorGroups] = useState<DoorGroup[]>([]);
	const [functionCategory, setFunctionCategory] = useState<number>(-1);
	const [functionGroup, setFunctionGroup] = useState<number>(-1);
	const [functionS, setFunctionS] = useState<number>(-1);
	const [showConflictMsgModal, setShowConflictMsgModal] = useState<boolean>(false);
	const [conflictMsgModal, setConflictMsgModal] = useState<JSX.Element>(null);
	const [fetchedControlZones, setFetchedControlZones] = useState<Map<number, ControlZone[]>>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isTabDisabled, setIsTabDisabled] = useState<boolean>(false);
	const [deadmanTime, setDeadmanTime] = useState<number>(0);
	const [indexedCommandId, setIndexedCommandId] = useState<number>(1);
	const [threatLevel, setThreatLevel] = useState<number>(0);

	const addCredentialFunction = ({ credentialFunction, controlZoneIndex = -1, controllerIndex = -1 }: AddCredentialFunction) => {
		const selectedFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === functionCategory);
		const selectedFunction = selectedFunctionCategory.Functions.find(f => f.FunctionIndex === functionS);
		const newTableData: Partial<CredentialFunction>[] = credentialFunctions ? [...credentialFunctions] : [];
		switch (credentialFunction) {
			case CredentialFunctionEnum.DOOR_GROUP:
			case CredentialFunctionEnum.SPECIAL_DOOR_GROUP:
				if (doorGroup && doorGroups?.length > 0) {
					const [strDoorGroupId, strControllerIds, strName, strIsMaster] = doorGroup.split('%');
					const doorGroupId = parseInt(strDoorGroupId);
					const boolIsMaster = strIsMaster === 'true';
					const selectedDoorGroup = doorGroups.find(
						dg =>
							dg.DoorGroupId === doorGroupId &&
							dg.ControllerIds === strControllerIds &&
							dg.Name === strName &&
							dg.IsMasterDoorGroup === boolIsMaster
					);

					if (selectedDoorGroup && doorGroupId > -1) {
						const checkConflictsResult: CredentialCheckConflict = checkConflicts(selectedDoorGroup.ControllerIds, true);
						if (checkConflictsResult.Index > -1) {
							buildConflictMsgModal(checkConflictsResult, selectedDoorGroup.ControllerIds);
						} else {
							newTableData.push({
								ZoneName: selectedDoorGroup.Name,
								FunctionName: selectedFunction.DefaultFunctionName,
								FunctionIndex: selectedFunction.FunctionIndex,
								ControllerIds: selectedDoorGroup.ControllerIds,
								FunctionCategory: selectedFunctionCategory.FunctionCategoryIndex,
								HostZone: selectedDoorGroup.IsMasterDoorGroup ? -1 : selectedDoorGroup.DoorGroupId,
								MasterDoorGroupId: selectedDoorGroup.IsMasterDoorGroup ? selectedDoorGroup.DoorGroupId : -1,
								Zones: undefined,
								IsMasterDoorGroup: selectedDoorGroup.IsMasterDoorGroup,
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}

					const tempDgs = filterDoorGroups(newTableData, doorGroups);
					setLocalDoorGroups(tempDgs);
				}
				setDoorGroup('');
				break;

			case CredentialFunctionEnum.FUNCTION_GROUP:
				if (functionGroup > 0 && functionGroups?.length > 0) {
					const selectedFunctionGroup = functionGroups.find(fg => fg.FunctionGroupId === functionGroup);
					if (selectedFunctionGroup) {
						const { ControllerIds: controllerIds, FunctionGroupId, Name } = selectedFunctionGroup;
						const checkConflictsResult: CredentialCheckConflict = checkConflicts(controllerIds, false);
						if (checkConflictsResult.Index > -1) {
							buildConflictMsgModal(checkConflictsResult, controllerIds);
						} else {
							newTableData.push({
								ZoneName: Name,
								FunctionName: selectedFunction.DefaultFunctionName,
								FunctionIndex: selectedFunction.FunctionIndex,
								FunctionCategory: selectedFunctionCategory.FunctionCategoryIndex,
								HostZone: FunctionGroupId,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								Zones: undefined,
								ControllerIds: selectedFunctionGroup.ControllerIds,
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}
				}
				setFunctionGroup(-1);
				break;

			case CredentialFunctionEnum.INDEXED_COMMAND:
				if (controllers?.length > 0) {
					const indexedCommandOption = IndexedCommandsOptions.find(option => option.Id === indexedCommandId);
					const cController = controllers.find(c => c.ControllerId === controllerIndex);
					if (cController && indexedCommandOption) {
						const { ControllerId } = cController;
						const controllerIds = ControllerId.toString();
						const checkConflictsResult: CredentialCheckConflict = checkConflicts(controllerIds, false);

						if (checkConflictsResult.Index > -1) {
							buildConflictMsgModal(checkConflictsResult, controllerIds);
						} else {
							newTableData.push({
								ZoneName: `${indexedCommandOption.Name} (${cController.ControllerName})`,
								FunctionName: selectedFunction.DefaultFunctionName,
								FunctionIndex: selectedFunction.FunctionIndex,
								FunctionCategory: selectedFunctionCategory.FunctionCategoryIndex,
								HostZone: -1,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								Zones: [{ ControllerId, ZoneIndex: indexedCommandId, DeadmanTime: undefined }],
								ControllerIds: ControllerId.toString(),
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}
				}
				setController(-1);

				break;

			case CredentialFunctionEnum.THREAT_LEVEL:
				if (controllers?.length > 0 && threatLevels?.length > 0) {
					const threatLevelOption = threatLevels.find(option => option.ThreatId === threatLevel);
					const cController = controllers.find(c => c.ControllerId === controllerIndex);
					if (cController && threatLevelOption) {
						const { ControllerId } = cController;
						const controllerIds = ControllerId.toString();
						const checkConflictsResult: CredentialCheckConflict = checkConflicts(controllerIds, false);

						if (checkConflictsResult.Index > -1) {
							buildConflictMsgModal(checkConflictsResult, controllerIds);
						} else {
							newTableData.push({
								ZoneName: `${threatLevelOption.Name} (${cController.ControllerName})`,
								FunctionName: selectedFunction.DefaultFunctionName,
								FunctionIndex: selectedFunction.FunctionIndex,
								FunctionCategory: selectedFunctionCategory.FunctionCategoryIndex,
								HostZone: -1,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								Zones: [{ ControllerId, ZoneIndex: threatLevel, DeadmanTime: undefined }],
								ControllerIds: ControllerId.toString(),
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}
				}
				setController(-1);

				break;

			default:
				if (controlZoneIndex > 0 && controllers?.length > 0 && controlZones?.length > 0) {
					const cControlZone = controlZones.find(cz => cz.ControlZoneIndex === controlZoneIndex);
					const cController = controllers.find(c => c.ControllerId === controllerIndex);
					if (cController && cControlZone) {
						const { ControlZoneIndex, ControlZoneName } = cControlZone;
						const { ControllerId } = cController;
						const controllerIds = ControllerId.toString();
						const checkConflictsResult: CredentialCheckConflict = checkConflicts(controllerIds, false);
						const deadmanTimeSec: number = credentialFunction === CredentialFunctionEnum.DEADMAN_TIMER ? deadmanTime : undefined;

						if (checkConflictsResult.Index > -1) {
							buildConflictMsgModal(checkConflictsResult, controllerIds);
						} else {
							newTableData.push({
								ZoneName: `${ControlZoneName} (${cController.ControllerName})`,
								FunctionName: selectedFunction.DefaultFunctionName,
								FunctionIndex: selectedFunction.FunctionIndex,
								FunctionCategory: selectedFunctionCategory.FunctionCategoryIndex,
								HostZone: -1,
								MasterDoorGroupId: -1,
								IsMasterDoorGroup: false,
								Zones: [{ ControllerId, ZoneIndex: ControlZoneIndex, DeadmanTime: deadmanTimeSec }],
								ControllerIds: ControllerId.toString(),
							});
							dispatcher(setCredentialFunctionsArrayAction(newTableData));
						}
					}
				}
				setControlZone(-1);
				break;
		}
	};

	useEffect(() => {
		setIsLoading(true);
		Promise.all([
			enrollmentApi.getFunctionCategories(),
			enrollmentApi.getOperatorDoorGroups(),
			enrollmentApi.getOperatorFunctionGroups(),
			enrollmentApi.getXboxControllers(),
		])
			.then(res => {
				const [functionCategories, doorGroups, functionGroups, controllers] = res;
				const defDg: DoorGroup = { ControllerIds: '', DoorGroupId: -1, IsMasterDoorGroup: false, Name: '' };
				const defFg: FunctionGroup = { ControllerIds: '', Defined: false, FunctionGroupId: -1, Name: '' };
				const dgs: DoorGroup[] = doorGroups?.length > 0 ? [defDg, ...doorGroups.sort((a, b) => a.Name.localeCompare(b.Name))] : [defDg];
				const fgs: FunctionGroup[] = functionGroups?.length > 0 ? [defFg, ...functionGroups] : [defFg];
				dispatcher(
					setFunctionTabServicesDataAction({
						controllers,
						doorGroups: dgs,
						functionCategories,
						functionGroups: fgs,
					})
				);
				if (functionCategories?.length > 0) setFunctionCategory(functionCategories[0].FunctionCategoryIndex);
				if (credentialFunctions?.length > 0) {
					setLocalDoorGroups(filterDoorGroups(credentialFunctions, dgs));
				} else {
					setLocalDoorGroups(dgs);
				}
				setIsLoading(false);
			})
			.catch(err => {
				console.error(err);
				dispatcher(
					setFunctionTabServicesDataAction({
						controllers: [],
						doorGroups: [],
						functionCategories: [],
						functionGroups: [],
					})
				);
			});
	}, []);

	useEffect(() => {
		if (functionCategories?.length > 0) {
			const selectedFunctionCategory = functionCategories[0];
			setFunctionCategory(selectedFunctionCategory.FunctionCategoryIndex);
			setFunctionS(selectedFunctionCategory.Functions[0].FunctionIndex);
		}
	}, [JSON.stringify(functionCategories)]);

	useEffect(() => {
		if (controllers && controllers.length > 0) {
			const controllerId = controllers[0].ControllerId;
			setControllerIdState(controllerId);
		}
	}, [JSON.stringify(controllers)]);

	useEffect(() => {
		if (functionCategories?.length > 0 && doorGroup) {
			if (functionCategory == 4 && (functionS === 25 || functionS === 26 || functionS === 27)) {
				addCredentialFunction({ credentialFunction: CredentialFunctionEnum.SPECIAL_DOOR_GROUP });
			} else {
				addCredentialFunction({ credentialFunction: CredentialFunctionEnum.DOOR_GROUP });
			}
		}
	}, [doorGroup]);

	useEffect(() => {
		if (functionCategories?.length > 0 && functionGroup > -1) {
			addCredentialFunction({ credentialFunction: CredentialFunctionEnum.FUNCTION_GROUP });
		}
	}, [functionGroup]);

	useEffect(() => {
		setIsTabDisabled(false);
		if (IsDeviceAdminCredentialTemplate && IDF === 0) setIsTabDisabled(true);
		else if (linkToCredential) setIsTabDisabled(true);
		else if (IDF === 0) setIsTabDisabled(true);
	}, [IDF]);

	useEffect(() => {
		if (saveConcatenatedData && IsDeviceAdminCredentialTemplate) {
			setIsTabDisabled(true);
		}
	}, [saveConcatenatedData]);

	useEffect(() => {
		linkToChkChanged();
	}, [linkToCredential]);

	useEffect(() => {
		if (fetchedCredential && modelEstablished) {
			setLocalDoorGroups(filterDoorGroups(credentialFunctions, doorGroups));
		}
	}, [fetchedCredential, modelEstablished]);

	const linkToChkChanged = () => {
		if (credentialTemplates?.length > 0) {
			if (linkToCredential) {
				setIsTabDisabled(true);
			} else {
				setIsTabDisabled(false);
			}
		}
	};

	const setControllerIdState = (controllerId: number): void => {
		getControlZones(controllerId);
		setController(controllerId);
	};

	const checkConflicts = (controllerIds: string, isAccessFunction: boolean): CredentialCheckConflict => {
		let dup: number = -1;
		let conflictNames: string = '';
		let conflictFunctionName: string = '';

		let controllerCount = 0;
		let cf: Partial<CredentialFunction>;
		let tableControllerIds: string[];
		let currentControllerIds: string[];
		for (let i = 0; i < credentialFunctions?.length; i++) {
			cf = credentialFunctions[i];
			tableControllerIds = cf.ControllerIds.split(',').filter(x => x !== '0');
			currentControllerIds = controllerIds.split(',');

			for (let j = 0; j < tableControllerIds.length; j++) {
				const currentTableCredentialIds = tableControllerIds[j];
				if (currentControllerIds.some(cv => cv === currentTableCredentialIds)) {
					if (conflictNames === '') {
						conflictNames = cf.ZoneName;
					} else {
						conflictNames += `, ${cf.ZoneName}`;
					}
					conflictFunctionName = cf.FunctionName;

					if (OverlapAccessFlag && isAccessFunction) {
						if (functionS !== cf.FunctionIndex) {
							dup = parseInt(tableControllerIds[j]);
						}
						controllerCount++;
						if (controllerCount >= CustomAccessZones) {
							dup = parseInt(tableControllerIds[j]);
							return { Index: dup, DeviceNames: conflictNames, FunctionName: conflictFunctionName };
						}
					} else {
						dup = i;
					}
				}
			}
		}

		return { Index: dup, DeviceNames: conflictNames, FunctionName: conflictFunctionName };
	};

	const getControlZones = async (controllerId: number) => {
		try {
			let controlZones: ControlZone[] = [];
			const currentControlZones = new Map(fetchedControlZones);
			if (currentControlZones.has(controllerId) && currentControlZones.get(controllerId)?.length > 0) {
				controlZones = currentControlZones.get(controllerId);
			} else {
				controlZones = await deviceAdminApi.retrieveControlZones(controllerId);
				currentControlZones.set(controllerId, controlZones || []);
				setFetchedControlZones(currentControlZones);
			}
			dispatcher(setControlZonesArrayAction(controlZones));
		} catch (err) {
			console.error(err);
			dispatcher(setControlZonesArrayAction([]));
		}
	};

	const handleOnChangeFunctionCategory = (value: number) => {
		const selectedFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === value);
		setFunctionCategory(selectedFunctionCategory.FunctionCategoryIndex);
		setFunctionS(selectedFunctionCategory.Functions[0].FunctionIndex);
	};

	const handleOnChangeFunction = (value: number) => {
		if (functionCategory == 4 && (value === 28 || value === 29 || value === 41)) {
			setController(-1);
		}
		setFunctionS(value);
	};

	const removeTableData = (idx: number) => {
		if (credentialFunctions && credentialFunctions.length > 0) {
			let newTableData = [...credentialFunctions];
			newTableData.splice(idx, 1);
			dispatcher(setCredentialFunctionsArrayAction(newTableData));
			const tempDgs = filterDoorGroups(newTableData, doorGroups);
			setLocalDoorGroups(tempDgs);
		}
	};

	const handleOnChangeDoorGroup = (value: string) => {
		setDoorGroup(value);
		dispatcher(setRootModelPropertyAction({ hasChanged: true }));
	};

	const handleOnChangeFunctionGroup = (value: number) => {
		setFunctionGroup(value);
		dispatcher(setRootModelPropertyAction({ hasChanged: true }));
	};

	const columns: ColumnsProps<Partial<CredentialFunction>>[] = [
		{
			...buildColumn(_('Function'), 'FunctionName', '15em', 'start'),
		},
		{
			...buildColumn(_('GroupZoneName'), 'ZoneName', 'auto', 'start'),
		},
		{
			...buildColumn(`${_('Time')} (${_('Sec')})`, 'Zones', '7em', 'start'),
			render: (Zones: PanelZone[], record: CredentialFunctionsDataType) => {
				let content: string | number = '';
				if (record.FunctionCategory == 4 && record.FunctionIndex === 28 && Zones && Zones.length > 0) {
					content = Zones[0].DeadmanTime;
				}

				return <span>{content}</span>;
			},
		},
		{
			...buildColumn('', '', '30px', 'end'),
			render: (_, record: CredentialFunctionsDataType, idx: number) =>
				!isTabDisabled && (
					<DeleteFilled
						onClick={() => {
							removeTableData(idx);
						}}
					/>
				),
		},
	];

	const controlZoneDropdownComponent = (handleOnChange: (value: number) => void) => (
		<>
			<label htmlFor='controlZone'>{_('ControlZone')}:</label>
			{controlZones && (
				<SelectCommon
					notUseCurrentParse
					id='controlZone'
					className={styles.select}
					disabled={isTabDisabled}
					getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
					onChange={handleOnChange}
					options={controlZones.map(cz => ({
						key: cz.ControlZoneIndex,
						label: cz.ControlZoneName,
						value: cz.ControlZoneIndex,
					}))}
					style={{ width: 505 }}
					value={controlZone > -1 ? controlZone : ''}
					showSearch={true}
					filterOption={filterOption}
					virtual={true}
				/>
			)}
		</>
	);

	const controllerDropdownComponent = (handleOnChange: (value: number) => void) => (
		<>
			<label htmlFor='controller'>{_('Controller')}:</label>
			<SelectCommon
				notUseCurrentParse
				id='controller'
				className={styles.select}
				disabled={isTabDisabled}
				getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
				onChange={handleOnChange}
				options={controllers.map(controller => ({
					key: controller.ControllerId,
					label: controller.ControllerName,
					value: controller.ControllerId,
				}))}
				style={{ width: 505 }}
				value={controller > -1 ? controller : ''}
				showSearch={true}
				filterOption={filterOption}
				virtual={true}
			/>
		</>
	);

	const handleOnChangeControlZone = (credentialFunction: CredentialFunctionEnum, value: number): void => {
		setControlZone(value);
		if (functionCategories?.length > 0 && value > -1) {
			addCredentialFunction({ credentialFunction, controlZoneIndex: value, controllerIndex: controller });
		}
		dispatcher(setRootModelPropertyAction({ hasChanged: true }));
	};

	const renderControlZoneLayout = (): JSX.Element => {
		let dd: JSX.Element = null;
		if ((controllers && controllers.length > 0 && controlZones && controlZones.length > 0 && functionCategory == 1) || functionCategory == 2) {
			dd = (
				<>
					<div className={cx(styles.sections, styles.functionSections)}>{controllerDropdownComponent(setControllerIdState)}</div>
					<div className={cx(styles.sections, styles.functionSections)}>
						{controlZoneDropdownComponent((value: number) => handleOnChangeControlZone(CredentialFunctionEnum.CONTROL_ZONE, value))}
					</div>
				</>
			);
		} else if (functionCategory === 5 && functionGroups?.length > 0) {
			dd = (
				<div className={cx(styles.sections, styles.functionSections)}>
					<label htmlFor='FunctionGroup'>{_('FunctionGroup')}:</label>
					<div>
						<SelectCommon
							notUseCurrentParse
							id='FunctionGroup'
							className={styles.select}
							disabled={isTabDisabled}
							getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
							onChange={handleOnChangeFunctionGroup}
							options={functionGroups.map(fg => ({
								key: fg.FunctionGroupId,
								label: fg.Name || ' ',
								value: fg.FunctionGroupId,
							}))}
							style={{ width: 505 }}
							value={functionGroup}
							showSearch={true}
							filterOption={filterOption}
							virtual={true}
						/>
					</div>
				</div>
			);
		} else if (functionCategory == 4 && (functionS === 28 || functionS === 29 || functionS === 41)) {
			switch (functionS) {
				case 28: // Deadman timer
					dd = (
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>{controllerDropdownComponent(setControllerIdState)}</div>
							<div className={styles.deadmanTimer}>
								{controlZoneDropdownComponent((value: number) => handleOnChangeControlZone(CredentialFunctionEnum.DEADMAN_TIMER, value))}
								<label htmlFor='deadmanTimerDropdown'>
									{_('Time')} ({_('Sec')}):
								</label>
								<InputNumber
									className={styles.inputNumber}
									disabled={isTabDisabled}
									id='deadmanTimerDropdown'
									max={MAX_DEADMAN_SECONDS}
									min={0}
									onChange={(value: number) => {
										setDeadmanTime(value);
									}}
									value={deadmanTime}
									formatter={noneDecimalFormatter}
								/>
							</div>
						</div>
					);
					break;

				case 29: // Indexed command
					dd = (
						<div className={styles.indexedCommand}>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='indexedCommandDropdown'>{_('IndexedCommand')}:</label>
								<SelectCommon
									notUseCurrentParse
									id='indexedCommandDropdown'
									className={styles.select}
									disabled={isTabDisabled}
									getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
									onChange={(value: number) => setIndexedCommandId(value)}
									options={IndexedCommandsOptions.map(option => ({
										key: option.Id,
										label: option.Name,
										value: option.Id,
									}))}
									style={{ width: 505 }}
									value={indexedCommandId}
									showSearch={true}
									filterOption={filterOption}
									virtual={true}
								/>
							</div>
							<div className={cx(styles.sections, styles.functionSections)}>
								{controllerDropdownComponent((value: number) => {
									setControllerIdState(value);
									addCredentialFunction({ credentialFunction: CredentialFunctionEnum.INDEXED_COMMAND, controllerIndex: value });
								})}
							</div>
						</div>
					);
					break;

				case 41: // Threat level
					dd = (
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='threatLevelDropdown'>{_('SetThreatLevel')}:</label>
								<SelectCommon
									notUseCurrentParse
									id='threatLevelDropdown'
									className={styles.select}
									disabled={isTabDisabled}
									getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
									onChange={(value: number) => setThreatLevel(value)}
									options={threatLevels.map((threatLevel: ThreatLevel) => ({
										key: threatLevel.ThreatId,
										label: threatLevel.Name,
										value: threatLevel.ThreatId,
									}))}
									style={{ width: 505 }}
									value={threatLevel}
									showSearch={true}
									filterOption={filterOption}
									virtual={true}
								/>
							</div>
							<div className={cx(styles.sections, styles.functionSections)}>
								{controllerDropdownComponent((value: number) => {
									setControllerIdState(value);
									addCredentialFunction({ credentialFunction: CredentialFunctionEnum.THREAT_LEVEL, controllerIndex: value });
								})}
							</div>
						</div>
					);
					break;
			}
		} else {
			dd = (
				<div className={cx(styles.sections, styles.functionSections)}>
					<label htmlFor='doorGroup'>{_('DoorGroup')}:</label>
					<div>
						<SelectCommon
							className={styles.select}
							id='doorGroup'
							disabled={isTabDisabled}
							getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
							onChange={handleOnChangeDoorGroup}
							options={localDoorGroups.map((dg: DoorGroup) => ({
								key: `${dg.DoorGroupId}%${dg.ControllerIds}%${dg.Name}%${dg.IsMasterDoorGroup}`,
								label: dg.Name || ' ',
								title: dg.Name,
								value: `${dg.DoorGroupId}%${dg.ControllerIds}%${dg.Name}%${dg.IsMasterDoorGroup}`,
							}))}
							style={{ width: 505 }}
							value={doorGroup}
							showSearch={true}
							filterOption={filterOption}
							virtual={true}
						/>
					</div>
				</div>
			);
		}
		return <div id='ddContainer'>{dd}</div>;
	};

	const buildConflictMsgModal = (checkConflictResult: CredentialCheckConflict, controllerIds: string): void => {
		let conflictMsgModal: JSX.Element = null;
		const fg = credentialFunctions[checkConflictResult.Index];

		conflictMsgModal = (
			<div className={styles.container}>
				<div className={styles.functionsConflictsModal}>
					<InfoCircleOutlined style={{ fontSize: '300%' }} />
					{OverlapAccessFlag ? (
						<p>
							{_('CredentialFunctionConflictMessage1').replace('%1', CCMVersionSupportsHRecord).replace('%2', CustomAccessZones.toString())}
							<br />
							<br />
							{_('CredentialFunctionConflictMessage2')}
							<br />
							<br />
							{_('CredentialFunctionConflictMessage3')
								.replace('%1', checkConflictResult.FunctionName)
								.replace('%2', checkConflictResult.DeviceNames)}
							{controllers.map((c: XboxController, idx: number) => {
								if (c.ControllerId.toString() === controllerIds) {
									return (
										<React.Fragment key={`c-${idx}`}>
											&nbsp;{_('Controller')}: {c.ControllerName}
										</React.Fragment>
									);
								} else return null;
							})}
						</p>
					) : (
						<p>
							{_('CredentialFunctionConflictMessage4').replace('%1', CCMVersionSupportsHRecord)}
							<br />
							<br />
							{_('CredentialFunctionConflictMessage5').replace('%1', CustomAccessZones.toString()).replace('%2', CCMVersionSupportsHRecord)}
							<br />
							<br />
							{_('CredentialFunctionConflictMessage2')}
							<br />
							<br />
							{_('CredentialFunctionConflictMessage3').replace('%1', fg.FunctionName).replace('%2', fg.ZoneName)}
						</p>
					)}
				</div>
			</div>
		);
		setConflictMsgModal(conflictMsgModal);
		setShowConflictMsgModal(true);
	};

	const footer = (
		<Button
			htmlType='button'
			onClick={() => {
				setShowConflictMsgModal(false);
			}}
			type='default'>
			{_('Ok')}
		</Button>
	);

	const getFunctionOptions = (): FunctionModel[] => {
		let options: FunctionModel[] = [];
		if (functionCategories?.length > 0 && functionCategory > -1) {
			const lFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === functionCategory);
			if (lFunctionCategory) options = lFunctionCategory.Functions;
		}
		return options;
	};

	return (
		<Spin spinning={isLoading} tip={`${_('Loading')}...`}>
			<div className={styles.container}>
				<Modal closable={false} footer={footer} visible={showConflictMsgModal}>
					{conflictMsgModal}
				</Modal>
				<div className={styles.form} id='functionDDContainer'>
					<div style={{ display: 'grid', gridTemplateColumns: '475px 475px' }}>
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='functionCategory'>{_('FunctionCategory')}:</label>
								<Select
									className={styles.select}
									disabled={isTabDisabled}
									id='functionCategory'
									getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
									onChange={handleOnChangeFunctionCategory}
									options={functionCategories?.map(fc => ({
										key: fc.FunctionCategoryIndex,
										label: fc.DefaultCategoryName,
										value: fc.FunctionCategoryIndex,
									}))}
									style={{ width: 240 }}
									value={functionCategory}
								/>
							</div>
							<div style={{ display: 'grid', gridTemplateColumns: '370px auto' }}>
								<div className={cx(styles.sections, styles.functionSections)}>
									<label htmlFor='functionS'>{_('Function')}:</label>
									<Select
										className={styles.select}
										disabled={isTabDisabled}
										id='functionS'
										getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
										onChange={handleOnChangeFunction}
										options={getFunctionOptions().map(f => ({
											key: f.FunctionIndex,
											label: f.DefaultFunctionName,
											value: f.FunctionIndex,
										}))}
										style={{ width: 240 }}
										value={functionS}
									/>
								</div>
							</div>
						</div>
						<div>
							<div style={{ marginTop: 7, minHeight: 33 }} />
							<div style={{ margin: '7px 0 0 -70px' }}>
								<label>{_('SelectFunctionMessage')}</label>
							</div>
						</div>
					</div>
					{renderControlZoneLayout()}
					<Table
						className={styles.functionsTable}
						columns={columns}
						dataSource={credentialFunctions?.map((cf, idx: number) => ({
							...cf,
							key: idx,
						}))}
						id={'credentialFunctionsTable'}
						pagination={false}
						scroll={{ y: '370px' }}
						size='small'
						style={{ height: 410, marginTop: 20, width: 920 }}
					/>
				</div>
			</div>
		</Spin>
	);
};

export default CredentialTemplateModalFunctionsTab;
